package com.vidaensupalabra.vsp.datos

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
data class BollsVerse(val pk: Int? = null, val verse: Int, val text: String)

object BibleRepository {
    private val jsonParser = Json { ignoreUnknownKeys = true }
    private val client = HttpClient(Js) {
        expectSuccess = false // Evita excepciones costosas en códigos 4xx/5xx y permite fallback limpio
        install(ContentNegotiation) {
            json(jsonParser)
        }
    }
    private val cache = mutableMapOf<String, String>()

    private val spanishToBollsLifeBookIdMap = mapOf(
        "Génesis" to 1, "Éxodo" to 2, "Levítico" to 3, "Números" to 4, "Deuteronomio" to 5,
        "Josué" to 6, "Jueces" to 7, "Rut" to 8, "1 Samuel" to 9, "2 Samuel" to 10,
        "1 Reyes" to 11, "2 Reyes" to 12, "1 Crónicas" to 13, "2 Crónicas" to 14, "Esdras" to 15,
        "Nehemías" to 16, "Ester" to 17, "Job" to 18, "Salmo" to 19, "Salmos" to 19,
        "Proverbios" to 20, "Eclesiastés" to 21, "Cantares" to 22, "Isaías" to 23, "Jeremías" to 24, "Lamentaciones" to 25,
        "Ezequiel" to 26, "Daniel" to 27, "Oseas" to 28, "Joel" to 29, "Amós" to 30,
        "Abdías" to 31, "Jonás" to 32, "Miqueas" to 33, "Nahúm" to 34, "Habacuc" to 35,
        "Sofonías" to 36, "Hageo" to 37, "Zacarías" to 38, "Malaquías" to 39,
        "Mateo" to 40, "Marcos" to 41, "Lucas" to 42, "Juan" to 43, "Hechos" to 44,
        "Romanos" to 45, "1 Corintios" to 46, "2 Corintios" to 47, "Gálatas" to 48, "Efesios" to 49,
        "Filipenses" to 50, "Colosenses" to 51, "1 Tesalonicenses" to 52, "2 Tesalonicenses" to 53,
        "1 Timoteo" to 54, "2 Timoteo" to 55, "Tito" to 56, "Filemón" to 57, "Hebreos" to 58,
        "Santiago" to 59, "1 Pedro" to 60, "2 Pedro" to 61, "1 Juan" to 62, "2 Juan" to 63,
        "3 Juan" to 64, "Judas" to 65, "Apocalipsis" to 66
    )

    private val sortedBookNames = spanishToBollsLifeBookIdMap.keys.sortedByDescending { it.length }

    private suspend fun fetchAndParseSingleChapter(bookId: Int, chapter: String): String? {
        val targetUrl = "https://bolls.life/get-text/RV1960/$bookId/$chapter/"
        
        val proxies = listOf(
            "https://corsproxy.io/?" to false,
            "https://api.codetabs.com/v1/proxy/?quest=" to false,
            "https://api.allorigins.win/raw?url=" to true
        )
        
        val encodedUrl = targetUrl.replace(":", "%3A").replace("/", "%2F")

        for ((proxyUrl, requiresEncode) in proxies) {
            try {
                val url = if (requiresEncode) "$proxyUrl$encodedUrl" else "$proxyUrl$targetUrl"
                
                val response = client.get(url)

                if (response.status.value in 200..299) {
                    val responseText = response.bodyAsText().trim()
                    // Validación estricta: asegurar que recibimos el Array JSON de Bolls.life, no un HTML de error
                    if (responseText.startsWith("[")) {
                        val verses = jsonParser.decodeFromString<List<BollsVerse>>(responseText)
                        return verses.joinToString("\n") { 
                            "${it.verse} ${it.text.replace(Regex("<[^>]*>"), "").trim()}" 
                        }
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                println("WARN BibleRepository: Proxy falló -> $proxyUrl. Error: ${e.message}")
            }
        }
        println("WARN BibleRepository: Todos los proxies fallaron para la Biblia -> $targetUrl")
        return null
    }

    suspend fun getChapterText(reference: String): String {
        if (cache.containsKey(reference)) {
            return cache[reference]!!
        }

        return try {
            val bookName = sortedBookNames.find { reference.startsWith(it, ignoreCase = true) } ?: return "Libro no encontrado en la referencia: '$reference'."
            
            val chapterInfo = reference.substring(bookName.length).trim()
            // Ignoramos los versículos aislando lo que está antes de los dos puntos ":"
            val chapterPart = chapterInfo.substringBefore(":").trim().ifEmpty { "1" }
            val bookId = spanishToBollsLifeBookIdMap[bookName]!!
            
            val combinedText = StringBuilder()
            val normalizedChapterPart = chapterPart.replace(Regex("[–—]"), "-")
            
            if (normalizedChapterPart.contains("-")) {
                val parts = normalizedChapterPart.split("-").mapNotNull { it.trim().toIntOrNull() }
                if (parts.size == 2) {
                    val start = minOf(parts[0], parts[1])
                    val end = maxOf(parts[0], parts[1])
                    val safeEnd = minOf(end, start + 4)
                    for (c in start..safeEnd) {
                        val text = fetchAndParseSingleChapter(bookId, c.toString())
                        if (text != null) {
                            if (combinedText.isNotEmpty()) combinedText.append("\n\n")
                            combinedText.append("--- CAPÍTULO $c ---\n")
                            combinedText.append(text)
                        }
                    }
                    if (end > safeEnd) {
                        combinedText.append("\n\n[Nota: La lectura se limitó a 5 capítulos para proteger tu conexión y el rendimiento. Continúa usando el botón de la parte superior]\n")
                    }
                } else {
                    val fallbackChapter = parts.firstOrNull()?.toString() ?: "1"
                    val text = fetchAndParseSingleChapter(bookId, fallbackChapter)
                    if (text != null) combinedText.append(text)
                }
            } else {
                // Nos aseguramos de mandar solo el dígito si hay texto extra (ej. "5 y 6")
                val singleChapter = normalizedChapterPart.takeWhile { it.isDigit() }.ifEmpty { "1" }
                val text = fetchAndParseSingleChapter(bookId, singleChapter)
                if (text != null) combinedText.append(text)
            }

            if (combinedText.isNotEmpty()) {
                if (cache.size > 50) cache.clear()
                cache[reference] = combinedText.toString()
                return combinedText.toString()
            }
            return "No se pudo cargar el texto bíblico. Revisa tu conexión."
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            println("ERROR BibleRepository: Error inesperado al procesar el capítulo bíblico '$reference' -> ${e.message}")
            "Error al cargar el capítulo. Por favor, usa el botón superior para leer en Bible.com."
        }
    }
}